import { DialogActions, DialogContent, DialogTitle, Stack, Tooltip } from '@mui/material';
import { ContainedButton, Dialog, OutlinedButton, TextInputField } from '@shared/components';
import { useAppTFunction } from '@retail/app/i18n';
import { FormProvider, useController, useForm } from 'react-hook-form';
import {
  CreateRoundingRuleSetForm,
  defaultPriceListValues,
  PriceListSchema,
  priceListSchema,
  PriceRoundingRuleConfigSchema,
  priceRoundingRuleConfigSchema,
  PriceRoundingSimulations,
} from '@retail/rounding/components';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { useEffect, useMemo, useRef } from 'react';
import { CreatePriceRoundingRuleConfigDTO, PriceRoundingRuleConfig } from '@retail/rounding/types';
import { useToast } from '@retail/hooks';
import { HelpOutline } from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRoundingRuleActions } from '@retail/rounding/context';
import { mapConfigToSchema, mapRulesSchemaToDto } from './mappers';

interface CreateMode {
  mode: 'CREATE';
  ruleConfig?: PriceRoundingRuleConfig;
}

interface UpdateMode {
  mode: 'UPDATE';
  ruleConfig: PriceRoundingRuleConfig;
}

interface StandardProps {
  open: boolean;
  onClose: () => void;
}

type Props = StandardProps & (CreateMode | UpdateMode);

const CreateOrUpdateRoundingRuleDialog = ({ open, onClose, mode, ruleConfig }: Props) => {
  const t = useAppTFunction();
  const toast = useToast();

  const {
    createRoundingRuleSet,
    updateRoundingRuleSet,
    simulatePrices,
    simulationResult,
    isLoadingRoundingRuleSetCreation,
    isLoadingUpdateRuleSet,
    isLoadingRoundingRuleSetSimulation,
    resetSimulationResult,
  } = useRoundingRuleActions();

  const defaultValues: Partial<PriceRoundingRuleConfigSchema> = useMemo(() => {
    switch (mode) {
      case 'CREATE': {
        if (ruleConfig) {
          return mapConfigToSchema(ruleConfig);
        }
        return { rules: [{ priceFrom: 0 }] } as Partial<PriceRoundingRuleConfigSchema>;
      }
      case 'UPDATE':
        return mapConfigToSchema(ruleConfig);
    }
  }, [mode, ruleConfig]);

  const formMethods = useForm<PriceRoundingRuleConfigSchema>({
    defaultValues,
    resolver: yupResolver(priceRoundingRuleConfigSchema(t)),
  });
  const { handleSubmit, control, watch } = formMethods;

  const nameController = useController({ name: 'name', control });
  const priceListFormMethods = useForm<PriceListSchema>({
    resolver: yupResolver(priceListSchema(t)),
    mode: 'onChange',
    defaultValues: defaultPriceListValues,
    shouldUnregister: true,
  });

  const previousPriceRoundingConfigValuesRef = useRef<string>();

  useEffect(() => {
    const subscription = watch((formValues) => {
      const currentValues = JSON.stringify(formValues);
      if (
        previousPriceRoundingConfigValuesRef.current &&
        previousPriceRoundingConfigValuesRef.current !== currentValues
      ) {
        resetSimulationResult();
      }
      previousPriceRoundingConfigValuesRef.current = currentValues;
    });
    return () => subscription.unsubscribe();
  }, [watch, resetSimulationResult]);

  const onSubmit = handleSubmit(({ name, rules }) => {
    const dto: CreatePriceRoundingRuleConfigDTO = { name, intervals: mapRulesSchemaToDto(rules) };

    switch (mode) {
      case 'CREATE': {
        return createRoundingRuleSet(dto).then(onClose);
      }
      case 'UPDATE': {
        return updateRoundingRuleSet(dto, ruleConfig.id).then(onClose);
      }
    }
  });

  const onSubmitSimulation = handleSubmit(({ rules }) => {
    const prices = priceListFormMethods.watch('prices');

    const validPrices = prices
      ?.map(({ price }) => Number(price.toString().replace(',', '.')))
      .filter((price) => typeof price === 'number');

    if (!validPrices || validPrices.length === 0) {
      toast.error(t('rounding.simulation.errors.noPrices'));
      return;
    }
    simulatePrices(validPrices, mapRulesSchemaToDto(rules));
  });

  const isLoading = isLoadingRoundingRuleSetCreation || isLoadingUpdateRuleSet;

  return (
    <Dialog maxWidth="xl" fullWidth open={open} onClose={onClose}>
      <DialogTitle>
        {mode === 'UPDATE'
          ? `${t('rounding.actions.editRule')} ${ruleConfig.name}`
          : t('rounding.actions.newRule')}
      </DialogTitle>

      <DialogContent
        sx={{
          paddingBottom: 0,
          borderBottom: (theme) => `1px solid ${theme.vars.palette.divider}`,
        }}
      >
        <Stack direction="row" mx={2}>
          <TextInputField
            {...nameController.field}
            error={nameController.fieldState.error?.message}
            label={t('rounding.ruleSetName.title')}
            size="small"
            margin="none"
            inputSx={{ minWidth: '250px' }}
          />
          <Tooltip title={t('rounding.ruleSetName.description')}>
            <HelpOutline aria-label={t('rounding.ruleSetName.description')} color="primary" />
          </Tooltip>
        </Stack>
        <FormProvider {...formMethods}>
          <CreateRoundingRuleSetForm />
        </FormProvider>
      </DialogContent>

      <DialogActions
        sx={{ justifyContent: 'space-between', alignItems: 'flex-end' }}
        disableSpacing
      >
        <FormProvider {...priceListFormMethods}>
          <PriceRoundingSimulations
            simulationResultPrices={simulationResult?.prices}
            submitSimulation={onSubmitSimulation}
            isLoadingRoundingRuleSetSimulation={isLoadingRoundingRuleSetSimulation}
          />
        </FormProvider>
        <Stack direction="row" spacing={1}>
          <OutlinedButton size="small" onClick={onClose} loading={isLoading}>
            {t('common.cancel')}
          </OutlinedButton>

          <ContainedButton onClick={onSubmit} loading={isLoading} size="small">
            {t('common.save')}
          </ContainedButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default withDialogSuspenseErrorBoundary(CreateOrUpdateRoundingRuleDialog);
